import React, { useState, useEffect, useRef } from 'react';
import { PORTAL_SIDE_DRAWER_WIDTH_PX } from './constants/portalLayout';
import { geminiGenerateText } from './services/geminiService';

const GuidedTour = ({ isOpen, onClose, langKey, activeLang, setOpenPanel }) => {
  const [step, setStep] = useState(-1);
  const [coords, setCoords] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [tourCache, setTourCache] = useState({}); // Stores { langKey: [ {title, body}, ... ] }
  const [isBatchLoading, setIsBatchLoading] = useState(false);

  const tourData = [
    { id: 'station-nav-guide', enTitle: "Step 1", enText: "Please evaluate your school against these 7 Stations. These are the core focus areas of the Inclusion 2.0 framework." },
    { id: 'matrix-grid-guide', enTitle: "Step 2", enText: "For each station, select a maturity level from 1 to 5. Hover over the levels to see the IFIP benchmarks for each stage." },
    { id: 'narrative-input-guide', enTitle: "Step 3", enText: "Provide a supporting narrative here. This is the opportunity to demonstrate that that your organisation's policy is carried out in practice and reflects the lived experience of the students. This is Step 3." },
    { id: 'station-nav-guide', enTitle: "Step 3", enText: "A tick appears here when at least 100 characters have been added in the narrative part. This is not a suggested word-count, it is simply to remind you to complete every station." },
    { id: 'evidence-section-guide', enTitle: "Step 4", enText: "Upload evidence to the vault here. You can add tags once uploaded, so the same document can apply to more than one station." },
    { id: 'review-btn-guide', enTitle: "Step 5", enText: "When ready, click Begin Final Review. IFIP's system will perform a diagnostic check to help you polish your submission." },
    { id: 'handbook-guide', enTitle: "Guidance", enText: "Use these side drawers to access the Digital Handbook and Matrix guidance. You can also see and edit the information that IFIP added from your registration form." }
  ];

  const introText = "Welcome to your IFIP Accreditation Portal. This guide will take approximately 2 minutes.";
  const finalBody = "The tour is now complete. This won't play automatically again, but you can call me anytime by clicking the question mark button in the corner. Good luck with your application!";

  // Reset tour when opened
  useEffect(() => {
    if (isOpen) {
      setStep(-1);
      setIsExiting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(), 500);
  };

  const customScrollTo = (targetY, duration) => {
    const startingY = window.pageYOffset;
    const diff = targetY - startingY;
    let start;
    window.requestAnimationFrame(function step(timestamp) {
      if (!start) start = timestamp;
      const time = timestamp - start;
      const percent = Math.min(time / duration, 1);
      window.scrollTo(0, startingY + diff * percent);
      if (time < duration) window.requestAnimationFrame(step);
    });
  };

  // --- BACKGROUND BULK TRANSLATOR ---
  useEffect(() => {
    if (tourCache[langKey] || isBatchLoading) return;

    const translateFullTour = async () => {
      setIsBatchLoading(true);
      
      const welcomeTitle = activeLang.ui?.nav_welcome || "Welcome";
      const itemsToTranslate = [
        { t: welcomeTitle, b: introText },
        ...tourData.map(d => ({ t: d.enTitle, b: d.enText })),
        { t: "Finished", b: finalBody }
      ];

      const prompt = `You are a professional translator. Translate the following 9 items into ${langKey}. 
      Return the results in a strict sequence. For each item, provide: TITLE | BODY. 
      Separate each of the 9 items with the delimiter "ITEM_START".
      Do not add commentary.
      
      ITEMS:
      ${itemsToTranslate.map((item, i) => `${i}. TITLE: ${item.t} | BODY: ${item.b}`).join('\n')}`;

      try {
        const responseText = await geminiGenerateText(prompt);
        
        const rawItems = responseText.split("ITEM_START");
        const parsed = rawItems.map(raw => {
          const clean = raw.replace(/^\d+\.\s*/, '').trim();
          const [title, body] = clean.split('|');
          return { 
            title: title?.replace(/TITLE:/i, '').trim() || "", 
            body: body?.replace(/BODY:/i, '').trim() || clean 
          };
        }).filter(item => item.title || item.body);

        if (parsed.length >= 8) {
          setTourCache(prev => ({ ...prev, [langKey]: parsed }));
        }
      } catch (e) {
        console.error("Tour Translation Error", e);
      } finally {
        setIsBatchLoading(false);
      }
    };

    translateFullTour();
  }, [langKey]);

  // --- STEP NAVIGATION & SCROLLING ---
  useEffect(() => {
    if (isOpen && !isExiting) {
      const runStep = async () => {
        if (step === 2 || step === 3) setIsScrolling(true);

        if (step === 6) setOpenPanel('guidance');
        else if (step !== -1) setOpenPanel(null);

        if (step === -1 || step === 7) {
          setCoords(null);
          setIsScrolling(false);
        } else {
          const el = document.getElementById(tourData[step].id);
          if (step === 2) { 
            const offset = el.getBoundingClientRect().top + window.scrollY - 150;
            customScrollTo(offset, 1800);
          } else if (step === 3) {
            customScrollTo(0, 1800);
          }

          const delay = (step === 2 || step === 3) ? 1900 : 100; 
          setTimeout(() => {
            const freshEl = document.getElementById(tourData[step].id);
            if (freshEl) {
              freshEl.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
              requestAnimationFrame(() => {
                const rect = freshEl.getBoundingClientRect();
                if (step === 6) {
                  const isRTL = activeLang.dir === 'rtl';
                  setCoords({
                    top: 0,
                    left: isRTL ? 0 : window.innerWidth - PORTAL_SIDE_DRAWER_WIDTH_PX,
                    width: PORTAL_SIDE_DRAWER_WIDTH_PX,
                    height: window.innerHeight,
                  });
                } else {
                  setCoords({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
                }
                setIsScrolling(false);
              });
            } else {
              setIsScrolling(false);
            }
          }, delay);
        }
      };
      runStep();
    }
  }, [step, isOpen, isExiting]);

  if (!isOpen) return null;

  // Data Lookup from Cache
  const currentStepData = tourCache[langKey]?.[step + 1] || null;

  const getBubblePosition = () => {
    if (!coords) return { top: '30%', left: '50%', transform: 'translateX(-50%)', tail: 'none' };
    const bubbleWidth = 320;
    // Text can wrap; underestimate height and the bubble can still clip — keep this generous.
    const bubbleHeight = 260;
    const margin = 16;

    const clamp = (t, l) => ({
      top: Math.max(margin, Math.min(t, window.innerHeight - bubbleHeight - margin)),
      left: Math.max(margin, Math.min(l, window.innerWidth - bubbleWidth - margin)),
    });

    let top = coords.top + coords.height + 25;
    let left = coords.left + (coords.width / 2) - (bubbleWidth / 2);
    let tail = 'top';

    if (step === 4) {
      left = coords.left - bubbleWidth - 40;
      top = coords.top;
      tail = 'none';
      const c = clamp(top, left);
      return { top: c.top, left: c.left, tail };
    }
    // Step 5: anchor is only the submit block (not the full flex column). Prefer bubble to the
    // left of the target in the narrow evidence column; fall back to below/above if no room.
    if (step === 5) {
      const preferLeft = coords.left - bubbleWidth - 40;
      if (preferLeft >= margin) {
        tail = 'none';
        const c = clamp(coords.top, preferLeft);
        return { top: c.top, left: c.left, tail };
      }
    }
    if (step === 6) {
      const isRTL = activeLang.dir === 'rtl';
      left = isRTL ? 520 : window.innerWidth - PORTAL_SIDE_DRAWER_WIDTH_PX - 340;
      top = 100;
      tail = 'none';
      const c = clamp(top, left);
      return { top: c.top, left: c.left, tail };
    }
    if (top + bubbleHeight > window.innerHeight) {
      top = coords.top - bubbleHeight - 25;
      tail = 'bottom';
    }
    const c = clamp(top, left);
    return { top: c.top, left: c.left, tail };
  };

  const pos = getBubblePosition();
  const isVisible = (coords || step === -1 || step === 7) && !isScrolling && !isExiting;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 10000 }}>
      <div onClick={handleClose} style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        background: 'rgba(0,0,0,0.75)', cursor: 'pointer',
        clipPath: (coords && !isScrolling && !isExiting) 
          ? `polygon(0% 0%, 0% 100%, ${coords.left}px 100%, ${coords.left}px ${coords.top}px, ${coords.left + coords.width}px ${coords.top}px, ${coords.left + coords.width}px ${coords.top + coords.height}px, ${coords.left}px ${coords.top + coords.height}px, ${coords.left}px 100%, 100% 100%, 100% 0%)`
          : 'none'
      }} />

      <div style={{
        position: 'absolute', background: 'white', padding: '25px', borderRadius: '15px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)', width: '320px', color: '#1a2a44',
        top: isExiting ? '85%' : pos.top, 
        left: isExiting ? '85%' : pos.left, 
        transform: isExiting ? 'scale(0.1)' : pos.transform,
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'all' : 'none',
        transformOrigin: 'bottom right'
      }}>
        {coords && pos.tail !== 'none' && (
          <div style={{
            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0, borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
            borderBottom: pos.tail === 'top' ? '15px solid white' : 'none',
            borderTop: pos.tail === 'bottom' ? '15px solid white' : 'none',
            top: pos.tail === 'top' ? '-15px' : 'auto',
            bottom: pos.tail === 'bottom' ? '-15px' : 'auto',
          }} />
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
             <img src="/images/01%20IFIP%20Logo.png" style={{ height: '24px' }} alt="IFIP" />
             <strong style={{ color: '#138a8a', fontSize: '0.8rem', textTransform: 'uppercase' }}>
               {currentStepData?.title || activeLang.ui?.guide_translating || "Translating..."}
             </strong>
          </div>
        </div>

        <div style={{ fontSize: '0.95rem', lineHeight: '1.6', minHeight: '60px' }}>
          {!currentStepData ? (
            <span className="translating-dots">{activeLang.ui?.guide_translating || "Translating Tour..."}</span>
          ) : (
            currentStepData.body
          )}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.85rem' }}>{activeLang.ui?.btn_close || "Close"}</button>
          <div style={{ display: 'flex', gap: '10px' }}>
            {step < 7 && (
              <button onClick={() => setStep(s => s + 1)} style={{ background: '#138a8a', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                {step === -1 ? (activeLang.ui?.btn_start_tour || "Start") : (activeLang.ui?.btn_next || "Next")}
              </button>
            )}
            {step === 7 && (
              <button onClick={handleClose} style={{ background: '#d11d7e', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>{activeLang.ui?.btn_got_it || "Got it!"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;